import { PageRouteConfig } from '@/modules/core/app/helpers/PageRouteConfig';
import { Module } from '@/modules/core/app/constants/app.constants';
import { getters, commits } from '@/modules/core/app/helpers/store';
import { actions as navActions } from '@/modules/core/nav/store/nav.actions';
import NavService from '@/modules/core/nav/services/NavService';

// Redirect route prefixes for form-related audit log redirects
const FORM_REDIRECT_PREFIXES = [
  '/entityfields/',
  '/entitygroups/',
  '/entitysteps/',
  '/entityforms/',
  '/entityratecards/',
];

// Helper function to check if path is a form redirect
function isFormRedirectPath(path) {
  // eslint-disable-next-line tap/no-raw-text-js
  if (!path) return false;
  // Exclude form detail pages (they have multiple path segments after the ID)
  // Form redirects are simple: /entityforms/{id} (just entity type and ID)
  const segments = path.split('/').filter(Boolean);
  // eslint-disable-next-line tap/no-raw-text-js
  if (segments[0] === 'entityforms' && segments.length === 2 && /^\d+$/.test(segments[1])) {
    return true;
  }
  // Check other redirect prefixes (entityfields, entitygroups, entitysteps)
  return FORM_REDIRECT_PREFIXES.some(
    // eslint-disable-next-line tap/no-raw-text-js
    (prefix) => prefix !== '/entityforms/' && path.startsWith(prefix)
  );
}

// Extend nav store action to support preserving nav key during form redirect
navActions.updateCurrentAppNavItem = function (context, { key = null, path }) {
  // When navigating to form redirect pages, preserve current nav and don't update
  if (isFormRedirectPath(path)) {
    const currentNavKey = getters.nav.getActiveNavItemKey();
    if (currentNavKey) {
      // eslint-disable-next-line tap/no-raw-text-js
      sessionStorage.setItem('preserveNavKey', currentNavKey);
    }
    return; // Don't update nav for this intermediate redirect page
  }

  // Check if navigation should preserve a specific nav key (e.g., from audit log redirect)
  // eslint-disable-next-line tap/no-raw-text-js
  const preservedNavKey = sessionStorage.getItem('preserveNavKey');
  if (preservedNavKey) {
    // eslint-disable-next-line tap/no-raw-text-js
    sessionStorage.removeItem('preserveNavKey');
    key = preservedNavKey;
  }

  if (!key) {
    key = NavService.resolveActiveAppNavItem(getters.nav.getAppNavItems(), path);
  }
  commits.nav.setCurrentNavItemKey(key);
};

const routes = [
  {
    path: 'entityforms/order',
    name: 'orderforms',
    component: () =>
      import(/* webpackChunkName: "order-forms-list-page" */ './pages/OrderFormsPage.vue'),
  },
  {
    path: 'entityforms/product',
    name: 'productforms',
    component: () =>
      import(/* webpackChunkName: "products-list-page" */ './pages/ProductsPage.vue'),
  },
  {
    path: 'entityforms/task',
    name: 'taskforms',
    component: () =>
      import(/* webpackChunkName: "task-forms-list-page" */ './pages/TaskFormsPage.vue'),
  },
  {
    path: 'entityforms/client',
    name: 'clientforms',
    component: () =>
      import(/* webpackChunkName: "client-forms-list-page" */ './pages/ClientFormsPage.vue'),
  },
  {
    path: 'entityforms/:formType/:id',
    name: 'entityforms',
    component: () =>
      import(
        /* webpackChunkName: "form-builder-page" */ '../formbuilder/pages/FormBuilderPage.vue'
      ),
    props: true,
  },
  {
    path: 'entityforms/:formType/details/:id/:tabValue?',
    name: 'entityform-details',
    component: () =>
      import(
        /* webpackChunkName: "entityform-details" */ './pages/EntityFormConfigurationDetailsPage.vue'
      ),
    props: true,
  },
  {
    path: '/entityforms/:formType/:id/workflow',
    name: 'workflow-creation',
    component: () =>
      import(
        /* webpackChunkName: "entity-status-set-creation-page" */ '../entitystatus/pages/EntityStatusSetCreationPage.vue'
      ),
    props: true,
  },
  {
    path: 'entityfields/:id',
    name: 'entityfield-redirect',
    component: () =>
      import(/* webpackChunkName: "entity-form-redirect" */ './pages/EntityFormRedirectPage.vue'),
    props: (route) => ({ id: route.params.id, entityType: 'entityfields' }),
  },
  {
    path: 'entitygroups/:id',
    name: 'entitygroup-redirect',
    component: () =>
      import(/* webpackChunkName: "entity-form-redirect" */ './pages/EntityFormRedirectPage.vue'),
    props: (route) => ({ id: route.params.id, entityType: 'entitygroups' }),
  },
  {
    path: 'entitysteps/:id',
    name: 'entitystep-redirect',
    component: () =>
      import(/* webpackChunkName: "entity-form-redirect" */ './pages/EntityFormRedirectPage.vue'),
    props: (route) => ({ id: route.params.id, entityType: 'entitysteps' }),
  },
  {
    path: 'entityforms/:id(\\d+)',
    name: 'entityform-redirect',
    component: () =>
      import(/* webpackChunkName: "entity-form-redirect" */ './pages/EntityFormRedirectPage.vue'),
    props: (route) => ({ id: route.params.id, entityType: 'entityforms' }),
  },
  {
    path: 'entityratecards/:id',
    name: 'entityratecard-redirect',
    component: () =>
      import(/* webpackChunkName: "entity-form-redirect" */ './pages/EntityFormRedirectPage.vue'),
    props: (route) => ({ id: route.params.id, entityType: 'entityratecards' }),
  },
];

// Process routes through PageRouteConfig
export const router = PageRouteConfig.factory(routes, Module.ENTITYFORM);
